import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ChatCircleDots, PaperPlaneTilt, X, Headset, Robot, ArrowClockwise } from "@phosphor-icons/react";
import useInactivityTimer from "@/hooks/useInactivityTimer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Iter151 — Chat memory in sessionStorage (non localStorage): la conversazione
// si resetta a ogni chiusura del browser / uscita dall'app. La choice del nome
// bot (Ludovica/Marta) segue lo stesso lifecycle così l'utente non vede una
// "vecchia" identità Cross-sessione.
const BOT_NAMES = ["Ludovica", "Marta"];
const getBotName = () => {
  let n = sessionStorage.getItem("ht_chatbot_name");
  if (!n || !BOT_NAMES.includes(n)) {
    n = BOT_NAMES[Math.floor(Math.random() * BOT_NAMES.length)];
    sessionStorage.setItem("ht_chatbot_name", n);
  }
  return n;
};

const getSessionKey = () => {
  let k = sessionStorage.getItem("ht_chatbot_session");
  if (!k) {
    k = `pub_${Math.random().toString(36).slice(2)}_${Date.now()}`;
    sessionStorage.setItem("ht_chatbot_session", k);
  }
  return k;
};

export default function ChatbotWidget() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [requiresHuman, setRequiresHuman] = useState(false);
  const scrollRef = useRef();
  const sessionKey = useRef(getSessionKey());
  const botName = useRef(getBotName());

  // ⏱ Reset chat pubblica dopo 20 min di inattività.
  const inactivity = useInactivityTimer({
    enabled: open,
    hasMessages: msgs.length > 0,
    isBusy: sending,
    onReset: () => {
      // Svuota UI + rigenera sessione lato client. Il backend creerà una
      // nuova sessione al prossimo `ask` con la nuova session_key.
      setMsgs([]);
      setRequiresHuman(false);
      const newKey = `pub_${Math.random().toString(36).slice(2)}_${Date.now()}`;
      try { sessionStorage.setItem("ht_chatbot_session", newKey); } catch {}
      sessionKey.current = newKey;
    },
  });

  const scrollBottom = () => {
    setTimeout(() => scrollRef.current?.scrollTo({ top: 999999, behavior: "smooth" }), 60);
  };

  const loadHistory = async () => {
    try {
      const r = await axios.get(`${API}/public/chatbot/history?session_key=${sessionKey.current}`);
      setMsgs(r.data?.messages || []);
      setRequiresHuman(!!r.data?.session?.escalated || !!r.data?.session?.requested_contact);
      scrollBottom();
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.warn("[chatbot] history load failed:", e);
    }
  };

  useEffect(() => { if (open) loadHistory(); }, [open]);

  // Polling più rapido quando siamo in modalità operatore, per ricevere risposte admin
  useEffect(() => {
    if (!open || !requiresHuman) return;
    const t = setInterval(loadHistory, 8000);
    return () => clearInterval(t);
  }, [open, requiresHuman]);

  const send = async (e) => {
    e?.preventDefault();
    if (!input.trim() || sending) return;
    const body = input.trim();
    setInput("");
    setMsgs(m => [...m, { role: "user", body, _id: `tmp_${Date.now()}` }]);
    scrollBottom();
    setSending(true);
    try {
      const r = await axios.post(`${API}/public/chatbot/ask`, {
        session_key: sessionKey.current,
        message: body,
        page_source: window.location.pathname,
        bot_name: botName.current,
      });
      if (r.data?.response) {
        setMsgs(m => [...m, { role: "assistant", body: r.data.response, _id: `bot_${Date.now()}` }]);
      }
      if (r.data?.requires_human) setRequiresHuman(true);
      scrollBottom();
    } catch (err) {
      setMsgs(m => [...m, { role: "assistant", body: "Errore di connessione. Riprova più tardi.", _id: `err_${Date.now()}` }]);
    } finally { setSending(false); }
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full shadow-lg flex items-center justify-center text-white ht-gradient hover:scale-105 transition-transform"
          aria-label="Apri chat"
          data-testid="chatbot-open-btn"
        >
          <ChatCircleDots size={28} weight="duotone" />
        </button>
      )}
      {open && (
        <div className="fixed bottom-6 right-6 z-40 w-[92vw] max-w-[380px] h-[70vh] max-h-[560px] bg-white rounded-2xl shadow-2xl border flex flex-col overflow-hidden font-sans" data-testid="chatbot-widget">
          <div className="ht-gradient text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-white/25 flex items-center justify-center">
                {requiresHuman ? <Headset size={18} weight="duotone" /> : <Robot size={18} weight="duotone" />}
              </div>
              <div>
                <div className="font-semibold text-sm">{botName.current}</div>
                <div className="text-[10px] opacity-90">
                  Rispondo subito · IT
                </div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="p-1 hover:bg-white/15 rounded" data-testid="chatbot-close-btn">
              <X size={16} weight="bold" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2 bg-[color:hsl(var(--surface-alt))]">
            {msgs.length === 0 && (
              <div className="text-center py-6 text-xs text-muted-foreground">
                <Robot size={32} weight="duotone" className="mx-auto text-purple-400 mb-2" />
                Ciao 👋 Chiedimi qualsiasi cosa su Housetag.
              </div>
            )}
            {msgs.map(m => {
              const isUser = m.role === "user";
              const isAdmin = m.role === "admin";
              // Iter191 — Markdown per assistant/admin, plain-text (con
              // whitespace-pre-wrap) per i messaggi dell'utente.
              const useMarkdown = !isUser;
              return (
                <div key={m._id} className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] px-3 py-2 rounded-lg text-sm leading-snug font-sans ${
                    useMarkdown ? "" : "whitespace-pre-wrap"
                  } ${
                    isUser ? "ht-gradient text-white" :
                    isAdmin ? "bg-emerald-100 text-emerald-900 border border-emerald-200" :
                    "bg-white border"
                  }`}>
                    {isAdmin && <div className="flex items-center gap-1 text-[10px] font-semibold text-emerald-700 mb-0.5"><Headset size={11} /> Operatore</div>}
                    {useMarkdown ? (
                      <div className="prose prose-sm max-w-none prose-slate font-sans prose-p:my-1.5 prose-ul:my-1.5 prose-ol:my-1.5 prose-li:my-0.5 prose-headings:mt-2 prose-headings:mb-1 prose-strong:text-inherit prose-strong:font-semibold prose-a:text-purple-700">
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={{
                            a: ({ node, ...props }) => (
                              <a {...props} target="_blank" rel="noopener noreferrer" />
                            ),
                          }}
                        >
                          {m.body || ""}
                        </ReactMarkdown>
                      </div>
                    ) : (
                      m.body
                    )}
                  </div>
                </div>
              );
            })}
            {sending && (
              <div className="flex justify-start"><div className="bg-white border px-3 py-2 rounded-lg text-xs text-muted-foreground">Sto scrivendo…</div></div>
            )}
          </div>

          {inactivity.pendingReset && (
            <div
              className="border-t border-amber-200 bg-amber-50 px-3 py-2.5 flex items-center gap-2 text-xs"
              data-testid="chatbot-inactivity-banner"
            >
              <span className="flex-1 text-amber-900">
                Sessione inattiva da 20 minuti.
              </span>
              <button
                type="button"
                onClick={inactivity.dismiss}
                className="px-2.5 py-1 rounded-md bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-[11px] font-semibold"
                data-testid="chatbot-continue-btn"
              >
                Continua
              </button>
              <button
                type="button"
                onClick={inactivity.confirmReset}
                className="px-2.5 py-1 rounded-md ht-gradient border-0 text-white text-[11px] font-semibold inline-flex items-center gap-1"
                data-testid="chatbot-new-chat-btn"
              >
                <ArrowClockwise size={11} weight="bold" /> Nuova chat
              </button>
            </div>
          )}

          <form onSubmit={send} className="p-3 border-t flex gap-2 bg-white">
            <input value={input} onChange={e => { setInput(e.target.value); inactivity.bump(); }}
              placeholder="Scrivi un messaggio…"
              className="flex-1 h-10 border rounded-md px-3 text-sm font-sans focus:outline-none focus:ring-2 focus:ring-purple-400"
              data-testid="chatbot-input" />
            <button type="submit" disabled={sending || !input.trim()}
              className="h-10 w-10 ht-gradient border-0 text-white rounded-md flex items-center justify-center disabled:opacity-50"
              data-testid="chatbot-send-btn">
              <PaperPlaneTilt size={16} weight="bold" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
